import { useState } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { useSC, Drawer, Card, Kpi, money, num, fmtDate, monthLabel, Loading } from "./parts";
import { ClientsTable } from "./ClientsTable";

export function DietitianProfile({ id, onClose, onOpenClient }) {
  const { data: d, loading } = useSC(`/dietitians/${id}`);
  const [scen, setScen] = useState("");
  return (
    <Drawer onClose={onClose} testid="sc-dietitian-profile">
      {loading || !d ? <Loading /> : (
        <div className="space-y-4">
          <div>
            <div className="font-mono text-xs" style={{ color: "var(--text-secondary)" }}>{d.id} · ★ {d.rating}</div>
            <h2 className="font-display text-3xl" style={{ color: "var(--primary)" }}>{d.name}</h2>
            <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{d.title}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi testid="sc-dt-total" label="Assigned clients" value={num(d.total)} sub={`${num(d.active)} active`} />
            <Kpi label="Inactive / completed" value={`${num(d.inactive)} / ${num(d.completed)}`} sub={`${d.onboarding} onboarding`} />
            <Kpi label="Plans delivered" value={num(d.delivered_plans)} sub={`${num(d.pending_plans)} pending`} />
            <Kpi testid="sc-dt-revenue" label="Revenue this FY" value={money(d.revenue_fy)} sub={`${num(d.renewals_fy)} renewals · ${d.renewing_now} renewing now`} accent="var(--red)" />
          </div>
          <Card title="Monthly performance">
            <div className="h-56"><ResponsiveContainer><ComposedChart data={d.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(20,20,43,0.08)" /><XAxis dataKey="month" tickFormatter={monthLabel} fontSize={11} />
              <YAxis yAxisId="l" fontSize={11} tickFormatter={(v) => `$${v / 1000}k`} /><YAxis yAxisId="r" orientation="right" fontSize={11} />
              <Tooltip formatter={(v, n) => (n === "New clients" ? v : money(v))} labelFormatter={monthLabel} /><Legend />
              <Bar yAxisId="l" dataKey="revenue" name="Revenue" fill="#14142B" radius={[4, 4, 0, 0]} />
              <Line yAxisId="r" dataKey="new_clients" name="New clients" stroke="#B93B3B" strokeWidth={2} />
            </ComposedChart></ResponsiveContainer></div>
          </Card>
          <div className="grid md:grid-cols-2 gap-4">
            <Card title="Pending tasks">
              {d.pending_tasks.map((t) => (
                <button key={t.label} data-testid={`sc-dt-task-${t.scenario || "overdue"}`} onClick={() => t.scenario && setScen(t.scenario)} className="w-full flex justify-between py-2 border-t first:border-t-0 text-sm text-left" style={{ borderColor: "var(--border-subtle)" }}>
                  <span>{t.label}</span><span className="font-bold">{num(t.count)}</span>
                </button>
              ))}
            </Card>
            <Card title={`Upcoming consultations (${num(d.upcoming_consults)} in 14 days)`}>
              {d.upcoming.slice(0, 7).map((u) => (
                <button key={u.cid} onClick={() => onOpenClient(u.cid)} className="w-full flex justify-between py-2 border-t first:border-t-0 text-sm text-left" style={{ borderColor: "var(--border-subtle)" }}>
                  <span>{u.name} <span className="text-xs opacity-60">· {u.goal}</span></span><span className="font-semibold">{fmtDate(u.date)}</span>
                </button>
              ))}
            </Card>
          </div>
          <Card title="Client database">
            <ClientsTable key={scen} dietitian={d.id} initialScenario={scen} onOpenClient={onOpenClient} testid="sc-dt-clients" />
          </Card>
        </div>
      )}
    </Drawer>
  );
}

export function PlanRecordDrawer({ plan, onClose, onOpenClient }) {
  return (
    <Drawer onClose={onClose} testid="sc-plan-record">
      <h2 className="font-display text-3xl mb-1" style={{ color: "var(--primary)" }}>{plan.name}</h2>
      <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>Every client this diet-plan template is assigned to. Your original template is never modified.</p>
      <Card><ClientsTable template={plan.id} onOpenClient={onOpenClient} testid="sc-plan-clients" /></Card>
    </Drawer>
  );
}
